
export enum VideoTypeEnum {
  PHOTO = "PHOTO",
  PANORAMA = "PANORAMA"
}

export enum RecordTypeEnum {
  PHOTO = "PHOTO",
  PANORAMA = "PANORAMA",
  UNKNOWN = "UNKNOWN"
}

export type VideoType = VideoTypeEnum.PHOTO | VideoTypeEnum.PANORAMA;
export type RecordType = RecordTypeEnum.UNKNOWN | RecordTypeEnum.PHOTO | RecordTypeEnum.PANORAMA;

export interface Campaign {
  id: number;
  type: 'PHOTO' | 'PANORAMA' | 'BOTH';
  network_uuid: string;
  network_slug: string;
  name: string;
  bitrix_id: number | null;
  status: 'planned' | 'running' | 'finished';
  start_date: string | null;
  end_date: string | null;
  cvat_project_id?: number;
  record_count?: number;
  video_count?: number;
  created_at: string;
  updated_at: string;
}

export interface CreateCampaignInput {
  type?: Campaign['type'];
  name: string;
  network_uuid: string;
  network_slug: string;
  bitrix_id: number;
  status: Campaign["status"];
}

export interface CvatProject {
  value: number;
  description: string;
  is_default: boolean;
}

export interface BitrixTask {
  id: number;
  name: string;
}

export interface User {
  uuid: string | null;
  email: string | null;
  username: string | null;
  is_staff: boolean;
  is_superuser: boolean;
}

export interface VideoRecord {
  id: number;
  campaign?: number;
  name: string;
  srid: number;
  date_time: string;
  network_uuid: string;
  network_slug?: string;
  videos: Video[];
  executions: Execution[];
  created_at: string;
  updated_at: string;
  user_id?: string;
  user_email?: string;
  type: RecordType;
  iteration?: number
}

export interface Video {
  id: number;
  camera: string;
  title: string;
  blob_name: string;
  url: string;
  content_type: string;
  size: number;
  uploaded_size: number;
  uploaded_at: string;
  is_uploaded: boolean;
  is_master: boolean;
  hash_id: string;
  type: VideoType;
  date_time?: string;
  camera_model?: string;
  camera_mode?: string;
  image_width?: number;
  image_height?: number;
}

export interface CalibrationVideo {
  id: number;
  camera: string;  // Camera unique_id (primary key)
  status: string;
  title: string;
  url: string;
  content_type: string;
  size: number;
  type: VideoType;
  date_time?: string;  // Optional date time field (matches Python model)
  intrinsics?: number[];  // Camera intrinsics matrix (3x3 flattened)
  distortion_coeffs?: number[];  // Distortion coefficients
  image_width?: number;  // Width of calibration images in pixels
  image_height?: number;  // Height of calibration images in pixels
  camera_model?: string;  // Camera model (e.g., "GoPro Hero 13")
  camera_mode?: string;  // Camera mode (e.g., "Linear", "Wide")
  calibrator_type?: string;  // Type of calibrator used (basic or advanced)
  detailed_error?: string;
}


export interface Execution {
  id: number;
  type: string;
  status: string;
  sub_status?: string | null;
  start_date: string;
  end_date: string | null;
  tasks: Task[];
  custom_data?: { argo_status_sync?: { state?: 'complete' | 'pending' } };
  user_email?: string | null;
  user_id?: string | null;
}

export interface Task {
  id: number;
  name: string;
  status: string;
  custom_status: string | null;
  sub_status?: string | null;
  detailed_error?: string;
  start_date: string | null;
  end_date: string | null;
}

export interface ExecutionStatus {
  status: "started" | "completed" | "error" | "canceled";
  error?: string;
}

export type JsonSchemaType = 'string' | 'number' | 'integer' | 'boolean';

export interface JsonSchemaProperty {
  type: JsonSchemaType;
  minimum?: number;
  maximum?: number;
  description?: string;
  default?: string | number | boolean;
}

export interface JsonSchema {
  type: string;
  properties: {
    [key: string]: JsonSchemaProperty;
  };
  required?: string[];
}

export interface Workflow {
  id: number;
  name: string;
  webhook_url: string;
  workflow_specific_data?: JsonSchema;
}

export interface WorkflowParams {
  [key: string]: string | number | boolean | object | undefined;
  execution_id?: number;
  workflow_name?: string;
  webhook_url: string;
}

export interface Network {
  uuid: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  nameLong: string;
  referential: { [key: string]: unknown };
  country: string;
  slug: string;
  boundingBox: { [key: string]: unknown };
  modules: unknown[];
  role: string;
  length: string;
  srid?: number;
}

export interface PhotoCollection {
  uuid: string;
  type: "PHOTO" | "PANORAMA";
  date: string;
  layer: string;
}

export interface Camera {
  unique_id: string;
  name: string;
  calibration_videos: CalibrationVideo[];
  used_calibration_video?: CalibrationVideo;
}

export interface WorkflowTemplateStep {
  key: string;
  label: string;
}

export interface WorkflowTemplateDefinition {
  key: string;
  label: string;
  description: string;
  steps: WorkflowTemplateStep[];
  disabled_record_types: string[];
}

export interface WorkflowCatalog {
  default_template: string;
  templates: WorkflowTemplateDefinition[];
}


// Helpers
function num(v: unknown, d = 0): number {
  const n = typeof v === 'string' && v.trim() === '' ? NaN : Number(v);
  return Number.isFinite(n) ? n : d;
}

function isObj(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object';
}

export class Pose {
  x = 0;
  y = 0;
  z = 1.05;
  roll = 0;
  pitch = -42.0;
  yaw = 20;

  constructor(init?: Partial<Pose>) {
    if (init) Object.assign(this, init);
  }

  static fromJSON(obj: unknown): Pose {
    const o = isObj(obj) ? obj : {};
    return new Pose({
      x: num(o.x), y: num(o.y), z: num(o.z),
      roll: num(o.roll), pitch: num(o.pitch), yaw: num(o.yaw),
    });
    // you could clamp angles here if needed
  }

  toJSON() {
    const { x, y, z, roll, pitch, yaw } = this;
    return { x, y, z, roll, pitch, yaw };
  }
}

export class CameraMatrix {
  fx = 0;
  fy = 0;
  cx = 0;
  cy = 0;
  skew = 0;

  constructor(init?: Partial<CameraMatrix>) {
    if (init) Object.assign(this, init);
  }

  static fromJSON(obj: unknown): CameraMatrix {
    const o = isObj(obj) ? obj : {};
    return new CameraMatrix({
      fx: num(o.fx), fy: num(o.fy),
      cx: num(o.cx), cy: num(o.cy),
      skew: num(o.skew),
    });
  }

  toJSON() {
    const { fx, fy, cx, cy, skew } = this;
    return { fx, fy, cx, cy, skew };
  }
}

export class DistortionCoefficients {
  k1 = 0;
  k2 = 0;
  p1 = 0;
  p2 = 0;
  k3 = 0;

  constructor(init?: Partial<DistortionCoefficients>) {
    if (init) Object.assign(this, init);
  }

  static fromJSON(obj: unknown): DistortionCoefficients {
    const o = isObj(obj) ? obj : {};
    return new DistortionCoefficients({
      k1: num(o.k1), k2: num(o.k2), p1: num(o.p1),
      p2: num(o.p2), k3: num(o.k3),
    });
  }

  toJSON() {
    const { k1, k2, p1, p2, k3 } = this;
    return { k1, k2, p1, p2, k3 };
  }
}


export class CameraCalibrationAndExtrinsics {
  camera_matrix!: CameraMatrix;
  distortion_coefficients!: DistortionCoefficients;
  pose!: Pose;
  image_width = 1920;
  image_height = 1080;

  constructor(init?: Partial<CameraCalibrationAndExtrinsics>) {
    if (init) Object.assign(this, init);
    // ensure nested objects exist
    if (!this.camera_matrix) this.camera_matrix = new CameraMatrix();
    if (!this.distortion_coefficients) this.distortion_coefficients = new DistortionCoefficients();
    if (!this.pose) this.pose = new Pose();
  }

  static fromJSON(obj: unknown): CameraCalibrationAndExtrinsics {
    const o = isObj(obj) ? obj : {};
    return new CameraCalibrationAndExtrinsics({
      camera_matrix: CameraMatrix.fromJSON(o.camera_matrix),
      distortion_coefficients: DistortionCoefficients.fromJSON(o.distortion_coefficients),
      pose: Pose.fromJSON(o.pose),
      image_width: num(o.image_width, 1920),
      image_height: num(o.image_height, 1080),
    });
  }

  toJSON() {
    return {
      calibration: {
        camera_matrix: this.camera_matrix.toJSON(),
        distortion_coefficients: this.distortion_coefficients.toJSON(),
      },
      pose: this.pose.toJSON(),
      resolution: {
        width: this.image_width,
        height: this.image_height
      },
      image_width: this.image_width,
      image_height: this.image_height,
    };
  }
}
